import { RepoError } from '@webapp/infrastructure/interfaces/repositories/RepoError'
import { HttpError } from '@webapp/infrastructure/interfaces/services/IHttpClient'

interface V1ErrorData {
  __all__?: string[]
  [key: string]: string[] | undefined
}

/**
 * V1 endpoints (e.g. `/api/permission/catalog/`, `/api/unit/`) return the
 * envelope `{ success, data, description, code }`. Failures surface either as
 * field errors under `data.__all__` OR as a top-level `description` on the
 * envelope — extract whichever is present.
 */
const getV1ErrorMessage = (body: unknown): string => {
  const envelope = body as
    | { data?: V1ErrorData; description?: string }
    | null
    | undefined
  return envelope?.data?.__all__?.[0] ?? envelope?.description ?? ''
}

/** Map an `HttpError` from a V1 endpoint into a `RepoError` (ADR-4). */
export const throwRepoErrorFromApiV1Error = (error: unknown): never => {
  if (error instanceof HttpError) {
    throw new RepoError({
      code: null,
      message: getV1ErrorMessage(error.response),
      status: error.status,
      response: error.response,
      headers: error.headers,
    })
  }

  throw error
}

/**
 * A V1 endpoint can return HTTP 200 with `success: false`; convert that into a
 * `RepoError` (arch-memory "V1 API Envelope Pattern").
 */
export const throwIfV1Failure = (success: boolean, body: unknown): void => {
  if (!success) {
    throw new RepoError({
      code: null,
      message: getV1ErrorMessage(body),
      status: 400,
      response: body,
    })
  }
}
